import { useMemo, useState } from "react";
import { SectionShell } from "@/pages/SectionShell";
import { Button } from "@/components/Button";
import { SelectField, TextAreaField } from "@/components/Field";
import { EmptyState } from "@/components/EmptyState";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { useCollection } from "@/lib/hooks/useCollection";
import { call, CALLABLES } from "@/services/callables";
import { formatDateTime } from "@/lib/format";
import {
  activeRequest,
  minutesRemaining,
  reasonIsSufficient,
  stateOf,
  STATE_KEY,
  STATE_TONE,
  SUPPORT_DURATIONS_HOURS,
  type SupportDurationHours,
  type SupportRequest,
} from "./lifecycle";
import type { Institution } from "@/types";

/**
 * Asking to be let in.
 *
 * The page opens by saying what it is not: Spontar cannot enter an institution
 * on its own authority, and the security rules are what make that true. A
 * console that merely hid the buttons would be a console whose promise depended
 * on nobody finding a URL.
 */
export function SupportPage() {
  const t = useT();
  const { locale } = useI18n();
  const { success, showError } = useToast();

  const { rows: institutions } = useCollection<Institution>("institutions");
  const { rows: requests, loading } = useCollection<SupportRequest>("supportRequests");

  const [institutionId, setInstitutionId] = useState("");
  const [reason, setReason] = useState("");
  const [durationHours, setDurationHours] = useState<SupportDurationHours>(1);
  const [busy, setBusy] = useState(false);

  const now = Date.now();
  const nameOf = useMemo(() => {
    const names = new Map(institutions.map((institution) => [institution.id, institution.name]));
    return (id: string) => names.get(id) ?? id;
  }, [institutions]);

  const ordered = useMemo(
    () => [...requests].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [requests]
  );
  const open = activeRequest(ordered, now);

  const canSend = institutionId !== "" && reasonIsSufficient(reason) && !busy;

  return (
    <SectionShell titleKey="support.title">
      {open ? (
        /*
          A persistent banner for the whole window. An admin who has forgotten
          they are inside somebody's institution is an admin who will look at
          one more thing than they were let in for.
        */
        <div role="status" className="mb-8 rounded-md border border-cue-green bg-cue-green-soft px-5 py-4">
          <p className="text-sm text-ink">
            {t("support.active.banner", {
              institution: nameOf(open.institutionId),
              minutes: minutesRemaining(open, now),
              reason: open.reason,
            })}
          </p>
          <p className="mt-1 text-sm text-ink-soft">{t("support.active.readOnly")}</p>
        </div>
      ) : null}

      <p className="max-w-2xl text-sm text-ink-soft">{t("support.intro")}</p>
      <p className="mt-2 max-w-2xl text-sm text-ink-muted">{t("support.readOnly")}</p>
      <p className="mt-1 max-w-2xl text-sm text-ink-muted">{t("support.noOperational")}</p>

      <section className="mt-8 max-w-xl">
        <h2 className="font-display text-lg text-ink">{t("support.request")}</h2>

        <div className="mt-4 flex flex-col gap-4">
          <SelectField
            labelKey="support.institution"
            value={institutionId}
            onChange={(event) => setInstitutionId(event.target.value)}
            options={[
              { value: "", labelKey: "common.none" },
              ...institutions.map((institution) => ({
                value: institution.id,
                labelKey: institution.name,
              })),
            ]}
          />

          <TextAreaField
            labelKey="support.reason"
            hintKey="support.reasonHint"
            value={reason}
            maxLength={400}
            onChange={(event) => setReason(event.target.value)}
          />

          <SelectField
            labelKey="support.duration"
            value={String(durationHours)}
            onChange={(event) =>
              setDurationHours(Number(event.target.value) as SupportDurationHours)
            }
            options={SUPPORT_DURATIONS_HOURS.map((hours) => ({
              value: String(hours),
              labelKey: `support.duration.${hours}`,
            }))}
          />

          <div>
            <Button
              busy={busy}
              disabled={!canSend}
              onClick={async () => {
                setBusy(true);
                try {
                  const result = await call<
                    { institutionId: string; reason: string; durationHours: number },
                    { notified: number }
                  >(CALLABLES.requestSupportAccess, {
                    institutionId,
                    reason: reason.trim(),
                    durationHours,
                  });
                  success("support.sent", { count: result.notified });
                  setReason("");
                } catch (error) {
                  showError(error);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {t("support.send")}
            </Button>
          </div>
        </div>
      </section>

      <section className="mt-12">
        {loading ? (
          <p className="text-sm text-ink-muted">{t("common.loading")}</p>
        ) : ordered.length === 0 ? (
          <EmptyState titleKey="support.empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">{t("support.title")}</caption>
              <thead>
                <tr className="border-b border-paper-deep text-left text-ink-muted">
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("support.column.institution")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("support.column.reason")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("support.column.asked")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("support.column.state")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("support.column.remaining")}</th>
                </tr>
              </thead>
              <tbody>
                {ordered.map((request) => {
                  const state = stateOf(request, now);
                  return (
                    <tr key={request.id} className="border-b border-paper-warm last:border-0">
                      <td className="px-3 py-3 text-ink">{nameOf(request.institutionId)}</td>
                      <td className="px-3 py-3 text-ink-soft">{request.reason}</td>
                      <td className="px-3 py-3 text-ink-muted">
                        {formatDateTime(locale, request.createdAt)}
                      </td>
                      <td className={`px-3 py-3 ${STATE_TONE[state]}`}>{t(STATE_KEY[state])}</td>
                      <td className="px-3 py-3 text-ink-muted">
                        {state === "approved"
                          ? t("support.remaining", { count: minutesRemaining(request, now) })
                          : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </SectionShell>
  );
}
